/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { jwtDecode } from 'jwt-decode';
import { useAuth } from '@workos-inc/authkit-react';

import { useGetMeQuery } from '@/store/api/users';
import { UserRole } from '@/types/users';
import { ViewerMeResponse } from '@/types/viewerMe';

type JwtPayload = {
  role?: string;
};

const normalizeRole = (role: string | undefined): UserRole | null => {
  if (!role) return null;
  const normalized = role.toLowerCase();
  return (normalized === 'administrator' ? 'admin' : normalized) as UserRole;
};

export type ViewerProfileContextValue = {
  me: ViewerMeResponse | undefined;
  role: UserRole | null;
  displayName: string;
  welcomeFirstName: string;
  jobTitle: string | null;
  profileImageUrl: string | null;
  dismissedBanners: string[];
  isRoleLoading: boolean;
  isProfileLoading: boolean;
  isProfileError: boolean;
};

export const defaultViewerProfileContextValue: ViewerProfileContextValue = {
  me: undefined,
  role: null,
  displayName: '',
  welcomeFirstName: 'User',
  jobTitle: null,
  profileImageUrl: null,
  dismissedBanners: [],
  isRoleLoading: false,
  isProfileLoading: false,
  isProfileError: false,
};

export const ViewerProfileContext = createContext<ViewerProfileContextValue>(
  defaultViewerProfileContextValue
);

export const ViewerProfileProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { user, getAccessToken } = useAuth();
  const userId = user?.id ?? null;

  const { data: me, isLoading, isError } = useGetMeQuery(undefined, { skip: !user });

  const getRole = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) {
      return null;
    }

    const decoded = jwtDecode<JwtPayload>(token);

    return normalizeRole(decoded.role);
  }, [getAccessToken]);

  const [role, setRole] = useState<UserRole | null>(null);
  const [isRoleLoading, setIsRoleLoading] = useState<boolean>(true);

  useEffect(() => {
    const run = async () => {
      if (!userId) {
        setRole(null);
        setIsRoleLoading(false);
        return;
      }

      setIsRoleLoading(true);
      try {
        const resolvedRole = await getRole();
        setRole(resolvedRole ?? null);
      } catch {
        setRole(null);
      } finally {
        setIsRoleLoading(false);
      }
    };

    void run();
  }, [getRole, userId]);
  const workosDisplayName = useMemo(
    () => [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim(),
    [user?.firstName, user?.lastName]
  );

  const displayName = useMemo(() => {
    const fromMe = me?.name?.trim();
    if (fromMe) return fromMe;
    return workosDisplayName;
  }, [me?.name, workosDisplayName]);

  const welcomeFirstName = useMemo(() => {
    const trimmed = displayName.trim();
    if (!trimmed) return 'User';
    const first = trimmed.split(/\s+/)[0];
    return first || 'User';
  }, [displayName]);

  const value = useMemo<ViewerProfileContextValue>(
    () => ({
      me,
      role,
      displayName,
      welcomeFirstName,
      jobTitle: me?.jobTitle ?? null,
      profileImageUrl: me?.profileImageUrl ?? null,
      dismissedBanners: me?.dismissedBanners ?? [],
      isRoleLoading,
      isProfileLoading: Boolean(user && isLoading),
      isProfileError: isError,
    }),
    [me, role, displayName, welcomeFirstName, isRoleLoading, isLoading, isError, user]
  );

  return <ViewerProfileContext.Provider value={value}>{children}</ViewerProfileContext.Provider>;
};
